import { ReportItem, ReportItemContent, ReportTestItem } from './entities';
import convertTestToReportItem from './convertTestItem';
import { customCompare } from './sorter';
import { countTreeItems } from './utils';
import { hasSubcategoryTier } from '@/app/features/testsuites/specifications/subcategory-tier';

export interface ConvertCategoryToTreeOptions {
  /**
   * Nest a Sub-category tier under each Category node instead of the flat, single-level shape
   * (default false, preserving the pre-existing behavior for any non-SDA caller of the
   * `category` groupMode). SDA reports are forced into Category grouping (raytd/web QA 108.03,
   * item 2) but still need Category -> Sub-category section boundaries/ordering internally, per
   * the same mirrors-existing rule convertTestsToTree already uses for its own category/
   * subcategory tiers: a tier-less spec (empty subcategory) nests directly under Category, no
   * Sub-category node at all.
   */
  nestSubcategories?: boolean;
}

export default function convertCategoryToTree(
  testItems: ReportTestItem[],
  { nestSubcategories = false }: ConvertCategoryToTreeOptions = {}
): ReportItem[] {
  const categoryMap: { [key: string]: ReportItem } = {};

  testItems.forEach((item) => {
    const category = item.testSpecification?.category || '(No Category)';
    const subcategory = item.testSpecification?.subcategory || '(No Subcategory)';

    if (!categoryMap[category]) {
      categoryMap[category] = nestSubcategories
        ? { id: category, title: category, children: [] }
        : { id: category, title: category, content: [] };
    }

    const categoryNode = categoryMap[category];
    const contentItem: ReportItemContent = convertTestToReportItem(item);

    if (!nestSubcategories) {
      categoryNode.content?.push(contentItem);
      return;
    }

    if (!hasSubcategoryTier(item.testSpecification?.subcategory)) {
      // Tier-less spec: nest content directly under Category (no Sub-category node), so
      // `content` and `children` are mutually exclusive per category node in this shape.
      categoryNode.content = categoryNode.content ?? [];
      categoryNode.content.push(contentItem);
      return;
    }

    let subcategoryNode = categoryNode.children?.find((c) => c.id === `${category}.${subcategory}`);
    if (!subcategoryNode) {
      subcategoryNode = {
        id: `${category}.${subcategory}`,
        title: subcategory,
        content: [],
      };
      categoryNode.children?.push(subcategoryNode);
    }
    subcategoryNode.content?.push(contentItem);
  });

  const sortedCategories = Object.values(categoryMap).sort((a, b) =>
    customCompare(a.title, b.title)
  );

  sortedCategories.forEach((categoryNode) => {
    categoryNode.content?.sort((a, b) => customCompare(a.title, b.title));
    categoryNode.children?.sort((a, b) => customCompare(a.title, b.title));
    categoryNode.children?.forEach((subcategoryNode) => {
      subcategoryNode.content?.sort((a, b) => customCompare(a.title, b.title));
    });
    countTreeItems(categoryNode);
  });

  return sortedCategories;
}
